'use client'

import { cn } from '@/lib/utils'
import * as React from 'react'
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip'
import { Label } from './label'
import { fieldTokens } from './field-tokens'

interface ReadOnlyInputProps extends Omit<React.ComponentProps<'input'>, 'readOnly' | 'disabled'> {
    tooltipText?: string
    label?: string
    subtext?: React.ReactNode
}

function ReadOnlyInput({ className, tooltipText, label, subtext, ...props }: ReadOnlyInputProps) {
	const [isHovered, setIsHovered] = React.useState(false)

	const inputElement = (
		<input
			type="text"
			readOnly
			data-slot="readonly-input"
            className={cn(
                fieldTokens.input,
                'cursor-default',
				className
			)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			{...props}
		/>
	)

	const wrappedInput = tooltipText ? (
		<Tooltip>
			<TooltipTrigger asChild>
				{inputElement}
			</TooltipTrigger>
			<TooltipContent side="bottom" className="max-w-xs">
				{tooltipText}
			</TooltipContent>
		</Tooltip>
	) : inputElement

    const inputContainer = (
        <div className="space-y-0">
            {wrappedInput}
            <div className="mt-0.5 min-h-[1.1rem]">
                {subtext && (
                    <p
                        className={cn(
                            "text-[0.9rem] italic leading-snug text-[#3A2E2F]/70 transition-opacity duration-200",
                            isHovered ? "opacity-100" : "opacity-0"
                        )}
                    >
                        {subtext}
                    </p>
                )}
            </div>
        </div>
    )

	if (label) {
		return (
            <div className="space-y-2">
				<Label className="text-sm">{label}</Label>
				{inputContainer}
			</div>
		)
	}

	return inputContainer
}

export { ReadOnlyInput }